/**
 * Incident Escalation Matrix Hook
 *
 * Loads 626.557 escalation tiers from backend rule configs.
 * Each tier defines severity level, required actions, deadlines,
 * and external reporting obligations.
 *
 * Data flow (target):
 *   Rules (rl-4-*) + RuleSets (rs-4-1-005) → GraphQL query
 *     → Escalation tiers with action lists and reporting requirements
 *
 * Current state: Falls back to structured reference data until
 * escalationMatrix resolver is wired (backward compat pattern).
 *
 * FERP Principles:
 * - Data-driven: Escalation rules from configs
 * - Life-critical: Mandated reporting = legal obligation
 * - Repository pattern: Data through GraphQL hooks
 */

import { gql, useQuery } from '@ferp/graphql-client';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface EscalationTier {
  severity: string;
  color: string;
  actions: string[];
  deadline: string;
  externalReport: boolean;
  reportTo?: string[];
}

export interface EmergencyContact {
  label: string;
  number: string;
}

export interface UseIncidentEscalationResult {
  tiers: EscalationTier[];
  emergencyContacts: EmergencyContact[];
  loading: boolean;
  error: Error | null;
}

// ─── GraphQL Query ────────────────────────────────────────────────────────────

const ESCALATION_MATRIX_QUERY = gql`
  query EscalationMatrix($subgraph: String!, $statute: String) {
    escalationMatrix(subgraph: $subgraph, statute: $statute) {
      severity
      color
      actions
      deadline
      externalReport
      reportTo
    }
    emergencyContacts(subgraph: $subgraph) {
      label
      number
    }
  }
`;

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useIncidentEscalation(options: {
  subgraph: string;
  statute?: string;
}): UseIncidentEscalationResult {
  const { data, loading, error } = useQuery<{
    escalationMatrix: EscalationTier[];
    emergencyContacts: EmergencyContact[];
  }>(ESCALATION_MATRIX_QUERY, {
    variables: options,
    errorPolicy: 'all',
  });

  // Backend data preferred. Fallback to seeded reference data
  // until escalationMatrix resolver is wired.
  // TODO: Remove fallback when resolver is live (DRR-070 backward compat pattern)
  const tiers = data?.escalationMatrix ?? REFERENCE_TIERS;
  const emergencyContacts = data?.emergencyContacts ?? REFERENCE_CONTACTS;

  return {
    tiers,
    emergencyContacts,
    loading,
    error: error ?? null,
  };
}

// ─── Reference Data (from MN 626.557 Vulnerable Adults Act) ──────────────────
// Fallback until rule configs (rs-4-1-005) are served via GraphQL.

const REFERENCE_TIERS: EscalationTier[] = [
  {
    severity: 'Near Miss / No Injury',
    color: 'blue',
    actions: ['Internal incident report', 'Root cause analysis', 'Prevention plan update'],
    deadline: 'Within shift',
    externalReport: false,
  },
  {
    severity: 'Minor Injury',
    color: 'yellow',
    actions: ['Internal incident report', 'Physician notification', 'Family notification', 'QA review'],
    deadline: 'Within 24 hours',
    externalReport: false,
  },
  {
    severity: 'Serious Injury',
    color: 'orange',
    actions: ['Internal incident report', 'MDH notification', 'Ombudsman notification', 'Family notification'],
    deadline: 'Within 24 hours',
    externalReport: true,
    reportTo: ['MDH Health Regulation Division', 'Office of Ombudsman for Long-Term Care'],
  },
  {
    severity: 'Suspected Maltreatment',
    color: 'red',
    actions: ['MAARC report (mandatory)', 'Internal incident report', 'Preserve evidence', 'Separate alleged perpetrator'],
    deadline: 'Within 24 hours (mandatory)',
    externalReport: true,
    reportTo: ['Minnesota Adult Abuse Reporting Center (MAARC)', 'Local law enforcement (if imminent danger)'],
  },
  {
    severity: 'Death',
    color: 'red',
    actions: ['MDH notification (immediate)', 'Ombudsman notification', 'Law enforcement', 'MAARC if maltreatment suspected'],
    deadline: 'Immediate',
    externalReport: true,
    reportTo: ['MDH Health Regulation Division', 'Office of Ombudsman', 'Local law enforcement', 'Medical examiner'],
  },
];

const REFERENCE_CONTACTS: EmergencyContact[] = [
  { label: 'MAARC (Mandated Reporting)', number: '1-844-880-1574' },
  { label: 'MDH Health Regulation', number: '651-201-4101' },
  { label: 'Ombudsman for LTC', number: '1-800-657-3591' },
];
